import { colors, Icon, IconButton, Tooltip, Typography } from "@mui/material";

import { DataGrid, GridColDef } from "@mui/x-data-grid";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

import { formatDate, formatDateEndOfDay } from "helpers/formatDate";

import { IListInscricoesActive } from "interfaces/inscricoes";
import InscricaoStatus from "./InscricaoStatus";
import { formatCurrency } from "helpers/utils";
import {
  canalInscricaoIcon,
  formatCanalInscricaoLabel,
} from "helpers/canalInscricao";

interface Props {
  isloading: boolean;
  selectedInscricao: (insc: IListInscricoesActive) => void;
  listInscricoes: IListInscricoesActive[];
}

export default function TableInscricoes({
  selectedInscricao,
  listInscricoes,
  isloading,
}: Props) {
  // definições das colunas da tabela das inscricoes
  const columns: GridColDef<IListInscricoesActive>[] = [
    {
      field: "action",
      headerName: "#",
      width: 50,
      disableColumnMenu: true,
      renderCell(params) {
        return (
          <MDBox display="flex" flexDirection="column">
            <IconButton onClick={() => selectedInscricao(params.row)}>
              <Icon>assignment_ind_outlined</Icon>
            </IconButton>
          </MDBox>
        );
      },
    },
    {
      field: "numeroInscricao",
      headerName: "N. inscrição",
      width: 150,
      renderCell(params) {
        return (
          <MDBox display="flex" flexDirection="column">
            <MDTypography variant="body2" sx={{ color: colors.blue[500] }}>
              {params.row.numeroInscricao}
            </MDTypography>
            <MDTypography variant="caption">
              {formatDate(params.row.dataInscricao, "dd/MM/yyyy HH:mm:ss")}
            </MDTypography>
          </MDBox>
        );
      },
    },
    {
      field: "nomeResponsavel",
      headerName: "Responsável",
      width: 220,
      renderCell(params) {
        return (
          <MDBox display="flex" flexDirection="column">
            <MDTypography variant="body2">
              {params.row.nomeResponsavel}
            </MDTypography>
            <MDTypography variant="caption">
              CPF: {params.row.cpfResponsavel}
            </MDTypography>
          </MDBox>
        );
      },
    },
    {
      field: "status",
      headerName: "Status",
      width: 150,
      align: "center",
      renderCell(params) {
        return <InscricaoStatus status={params.row.statusInscricao.tipo} />;
      },
    },
    {
      field: "canalInscricao",
      headerName: "Canal",
      width: 130,
      renderCell(params) {
        const label = formatCanalInscricaoLabel(
          params.row.canalInscricao,
          params.row.plataformaApp,
        );
        const icon = canalInscricaoIcon(
          params.row.canalInscricao,
          params.row.plataformaApp,
        );
        return (
          <MDBox display="flex" alignItems="center" gap={0.75}>
            <Icon fontSize="small" color="action">
              {icon}
            </Icon>
            <MDTypography variant="body2">{label}</MDTypography>
          </MDBox>
        );
      },
    },
    {
      field: "pagamentos",
      headerName: "Tipo",
      width: 110,
      renderCell(params) {
        const status = params.row.statusInscricao.tipo;
        const paymentIcon = params.row.pagamentos?.tipoPagamento?.icon;
        const isGratuitoOuCortesia =
          status === "Gratuito" || status === "Cortesia";
        const isCanceladoSemPagamento =
          status === "Cancelado" && !paymentIcon;
        const aguardandoPagamento =
          status === "Pendente" && !paymentIcon;

        return (
          <MDBox
            display="flex"
            flexDirection="column"
            justifyItems="center"
            alignItems="center"
          >
            <Tooltip
              title={
                isCanceladoSemPagamento
                  ? "Inscrição cancelada sem cobrança vinculada"
                  : aguardandoPagamento
                  ? "Cobrança ainda não gerada"
                  : `Vencimento: ${formatDateEndOfDay(params.row.pagamentos?.dataVencimento)}`
              }
            >
              {isGratuitoOuCortesia ? (
                <p style={{ textAlign: "center", fontWeight: "bold" }}>Gratuito</p>
              ) : isCanceladoSemPagamento ? (
                <p style={{ textAlign: "center", fontWeight: "bold" }}>Cancelado</p>
              ) : aguardandoPagamento ? (
                <Typography
                  variant="caption"
                  sx={{ textAlign: "center", fontWeight: "bold", px: 0.5 }}
                >
                  Aguardando pagamento
                </Typography>
              ) : paymentIcon ? (
                <img src={paymentIcon} alt="Pagamento" width={60} />
              ) : null}
            </Tooltip>
            {/* Se o tipo de pagamento for BOLETO  e o pagamento encontra-se com status "identified" */}
            {params.row.pagamentos?.tipoPagamento.id === 2 &&
              params.row.pagamentos?.cobrancaBoleto?.status ===
                "identified" && (
                <Typography
                  variant="caption"
                  color={colors.blue[500]}
                  fontWeight="bold"
                >
                  ⚠️ identificado
                </Typography>
              )}
            {/* Verificar se a inscrição CANCELADA existe pagamento */}
            {params.row.statusInscricao.tipo === "Cancelado" &&
              params.row.pagamentos?.dataBaixa && (
                <Typography
                  variant="caption"
                  color={colors.blue[500]}
                  fontWeight="bold"
                >
                  ⚠️ Pago
                </Typography>
              )}
          </MDBox>
        );
      },
    },
    {
      field: "qtdAtleta",
      headerName: "QTd Atletas",
      align: "center",
      renderCell(params) {
        return (
          <MDBox display="flex" flexDirection="column">
            <MDTypography variant="body2">
              {params.row._count.inscritos}
            </MDTypography>
          </MDBox>
        );
      },
    },
    {
      field: "desconto",
      headerName: "Desconto",
      align: "right",
      renderCell(params) {
        return (
          <MDBox display="flex" flexDirection="column">
            <MDTypography
              variant="caption"
              sx={{ color: colors.red[500], alignSelf: "end" }}
            >
              {formatCurrency(params.row.totalDesconto)}
            </MDTypography>
          </MDBox>
        );
      },
    },
    {
      field: "totais",
      headerName: "Totais",
      renderCell(params) {
        return (
          <MDBox display="flex" flexDirection="column">
            <MDTypography
              variant="caption"
              sx={{ color: colors.red[500], alignSelf: "end" }}
            >
              + {Number(params.row.taxa).toFixed(2)}
            </MDTypography>
            <MDTypography variant="inherit" sx={{ fontWeight: "400" }}>
              {formatCurrency(params.row.totalInscricao)}
            </MDTypography>
          </MDBox>
        );
      },
    },
    {
      field: "evento",
      headerName: "Evento",
      width: 350,
      renderCell(params) {
        return (
          <MDBox display="flex" flexDirection="column">
            <MDTypography variant="body2">
              {params.row.evento.titulo}
            </MDTypography>
            <MDTypography variant="caption">
              {formatDateEndOfDay(params.row.evento.dataEvento)}
            </MDTypography>
          </MDBox>
        );
      },
    },
    {
      field: "obs",
      headerName: "Obs",
      width: 250,
      renderCell(params) {
        return (
          <MDBox display="flex" flexDirection="column">
            <MDTypography sx={{ padding: 2 }} variant="caption">
              {params.row.obs}
            </MDTypography>
          </MDBox>
        );
      },
    },
  ];

  return (
    <DataGrid
      sx={{
        flexGrow: 1,
        "& .MuiDataGrid-columnHeaders": {
          backgroundColor: colors.grey[300],
        },
        "& .MuiDataGrid-row": {
          cursor: "pointer",
        },
        "& .MuiDataGrid-row:hover": {
          bgcolor: "rgba(249, 96, 48, 0.04)",
        },
      }}
      rows={listInscricoes}
      loading={isloading}
      columns={columns}
      onRowClick={(params) => selectedInscricao(params.row)}
      initialState={{
        pagination: { paginationModel: { page: 0, pageSize: 10 } },
      }}
      autoHeight={true}
      columnHeaderHeight={40}
      localeText={{
        noRowsLabel: "Nenhuma inscrição encontrada",
      }}
      density="comfortable"
      pageSizeOptions={[10, 20, 50, 100]}
      disableRowSelectionOnClick
    />
  );
}
